import { useState } from "react";
import { FlaskConical, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { JsonBlock } from "./JsonBlock";

export const TestOrderCard = ({ customerCode, itemCode, series, onDone }) => {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const send = async (localId) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/softone/test-order", { customer_code: customerCode, item_code: itemCode, qty_boxes: 1, local_id: localId });
      setResult(data);
      if (data.success) toast.success(`${t("softone.docNo")}: ${data.doc_no}`);
      else toast.error(data.error);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
      onDone();
    }
  };

  return (
    <section className="space-y-3 rounded-2xl bg-white p-5 shadow-sm" data-testid="softone-test-order">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-navy md:text-lg">{t("softone.testOrderTitle")}</h2>
          <p className="text-xs text-slate-500">{t("softone.testOrderSub", { series, customer: customerCode, item: itemCode })}</p>
        </div>
        <button type="button" disabled={busy} onClick={() => send(undefined)} data-testid="softone-create-test-order" className="flex h-11 items-center gap-2 rounded-full bg-brand px-5 text-sm font-semibold text-white transition-[background-color,transform] hover:bg-[#25a065] active:scale-[0.98] disabled:opacity-50">
          <FlaskConical className="h-4 w-4" /> {t("softone.createTestOrder")}
        </button>
        {result?.local_id && (
          <button type="button" disabled={busy} onClick={() => send(result.local_id)} data-testid="softone-retry-test-order" className="flex h-11 items-center gap-2 rounded-full border border-navy/15 px-5 text-sm font-semibold text-navy transition-colors hover:bg-canvas disabled:opacity-50">
            <RotateCcw className="h-4 w-4" /> {t("softone.retrySame")}
          </button>
        )}
      </div>
      {result && (
        <>
          <div className="flex flex-wrap gap-x-6 gap-y-1 rounded-xl bg-canvas px-4 py-3 text-sm">
            <span>{t("softone.docNo")}: <b className="text-navy" data-testid="softone-test-order-docno">{result.doc_no || "—"}</b></span>
            <span className="text-slate-500">local_id: {result.local_id}</span>
            {result.success && <span className={result.idempotent_hit ? "font-semibold text-amber-700" : "text-slate-500"} data-testid="softone-test-order-idempotent">{result.idempotent_hit ? t("softone.idempotentHit") : t("softone.newWrite")}</span>}
            {result.error && <span className="text-rose-600">{result.error}</span>}
          </div>
          {result.success && !result.idempotent_hit && (
            <div className="grid gap-3 lg:grid-cols-2">
              <JsonBlock title={t("softone.request")} data={result.request} testId="softone-test-order-request" />
              <JsonBlock title={t("softone.raw")} data={result.raw} testId="softone-test-order-raw" />
            </div>
          )}
        </>
      )}
    </section>
  );
};
